import { useEffect, useState } from 'react';
import { subscribeNotes } from '../../data/listeners/notes';
import { addNote, voidNote } from '../../data/commands/noteCommands';
import { validateTouch, withDefaults } from '../../data/proof';

const fmt = (ms) => new Date(ms).toLocaleString();
// Role badge so agent + manager notes are distinguishable on the shared timeline
// (T-U3-012). Older notes without a stored role show no badge.
const ROLE_BADGE = { agent: 'Agent', manager: 'Supervisor', owner: 'Owner', hqadmin: 'HQ' };

// Two-way notes timeline + proof-gated "Log Visit". Void-never-delete.
export default function Notes({ leadId, config, actor }) {
  const c = withDefaults(config);
  const [notes, setNotes] = useState([]);
  const [type, setType] = useState('Action');
  const [code, setCode] = useState('');
  const [text, setText] = useState('');
  const [hasProof, setHasProof] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    const unsub = subscribeNotes(leadId, setNotes, (e) => setError(e.message));
    return unsub;
  }, [leadId]);

  const codes = c.noteReasonCodes[type] ?? [];

  async function logVisit(e) {
    e.preventDefault();
    setError('');
    const gate = validateTouch(c, { hasProof, nextStep: text });
    if (!gate.ok) { setError(gate.errors.join(' ')); return; }
    try {
      await addNote(leadId, { type, code: code || null, text, hasProof }, actor);
      setText(''); setCode(''); setHasProof(false);
    } catch (err) {
      setError(err.message || 'Could not log the visit.');
    }
  }

  return (
    <div style={{ display: 'grid', gap: 'var(--space-3)' }}>
      <form onSubmit={logVisit} style={{ display: 'grid', gap: 8 }}>
        <div style={{ display: 'flex', gap: 8 }}>
          <select className="chip-select" value={type} onChange={(e) => { setType(e.target.value); setCode(''); }}>
            <option value="Action">Action</option>
            <option value="Status">Status</option>
          </select>
          <select className="chip-select" value={code} onChange={(e) => setCode(e.target.value)}>
            <option value="">Reason…</option>
            {codes.map((rc) => <option key={rc} value={rc}>{rc}</option>)}
          </select>
        </div>
        <textarea className="input" style={{ minHeight: 56, width: '100%' }} placeholder="Note / next step" value={text} onChange={(e) => setText(e.target.value)} />
        <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 'var(--text-small)', color: 'var(--color-fg-muted)' }}>
          <input type="checkbox" checked={hasProof} onChange={(e) => setHasProof(e.target.checked)} />
          Proof attached (photo / screenshot / signature){c.proofPolicy === 'required' ? ' — required' : ''}
        </label>
        {error && <div className="form-error">{error}</div>}
        <div><button type="submit" className="btn btn-primary btn-sm">Log Touchpoint</button></div>
      </form>

      <div style={{ display: 'grid', gap: 8 }}>
        {notes.map((n) => (
          <div key={n.id} style={{ borderLeft: `3px solid var(--color-border-mid)`, padding: '4px 10px', opacity: n.voided ? 0.5 : 1 }}>
            <div style={{ fontSize: 'var(--text-caption)', color: 'var(--color-fg-muted)' }}>
              {fmt(n.ts)} · {n.byName ?? n.byUid}
              {ROLE_BADGE[n.byRole] && <span className="badge tier-Small" style={{ margin: '0 4px' }}>{ROLE_BADGE[n.byRole]}</span>}
              · {n.type}{n.code ? ` · ${n.code}` : ''}{n.hasProof ? ' · 📎proof' : ''}
            </div>
            <div style={{ fontSize: 'var(--text-small)', textDecoration: n.voided ? 'line-through' : 'none' }}>{n.text}</div>
            {!n.voided && n.byUid === actor?.uid && (
              <button className="btn btn-ghost btn-sm" style={{ marginTop: 4 }} onClick={() => voidNote(leadId, n.id)}>Void</button>
            )}
          </div>
        ))}
        {notes.length === 0 && <div style={{ color: 'var(--color-fg-muted)', fontSize: 'var(--text-small)' }}>No notes yet.</div>}
      </div>
    </div>
  );
}
